import { EntityForm } from '@/common/entityFramework/entityForm/EntityForm';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { endDateValidator, startDateValidator } from '@/common/form/validators/dateValidators';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { useEffect, useState } from 'react';
import { PROD_SELECTIONS_FIELD, ProgProdProduct } from './formFields/ProgProdProduct';
import { ProgProdPriceBasis } from './formFields/ProgProdPymtBasis';
import { CalcTypeValue } from './formFields/value/CalcTypeValue';

// progObj may be supplied by an embedding parent (e.g. the Program workspace dialog); otherwise it is
// resolved from the route-level Program Price being viewed
export const ProgProdForm = ({ progObj: progObjProp, ...formProps }) => {
  const { entityObj } = useEntityMgr();
  const rsrc = useRsrc();
  const [progObj, setProgObj] = useState(progObjProp ?? null);
  const [hasTiers, setHasTiers] = useState(null);

  const embeddedAdd = formProps.mode === EntityFormMode.ADD;

  useEffect(() => {
    if (!progObj) return;
    setHasTiers(Boolean(progObj.tierBased));
  }, [progObj]);

  useEffect(() => {
    if (progObjProp) {
      setProgObj(progObjProp);
      return;
    }
    const progId = entityObj?.progId;
    if (!progId) return;
    rsrc.byId('md', 'PROG', progId).then((res) => setProgObj(res));
  }, [progObjProp, entityObj?.progId]);

  if (!progObj || hasTiers == null) return <></>;

  const augFieldDefs = (fieldToFieldDef) => {
    // Existing Tiers were priced off these fields, so they are locked until those Tiers are removed
    if (hasTiers) {
      fieldToFieldDef.progProdCalcType.editLevel = 'NEVER';
      fieldToFieldDef.pymtBasisId.editLevel = 'NEVER';
      fieldToFieldDef.conc.editLevel = 'NEVER';
    }

    fieldToFieldDef.pymtBasisId.component = <ProgProdPriceBasis fieldDef={fieldToFieldDef.pymtBasisId} />;
    fieldToFieldDef.conc.component = <CalcTypeValue fieldDef={fieldToFieldDef.conc} />;

    // Embedded add (dialog): pick one or many Products; the code is derived per Product on save
    if (embeddedAdd) {
      fieldToFieldDef.prodId.component = (
        <ProgProdProduct
          key="prodId"
          fieldDef={fieldToFieldDef.prodId}
          allowMultiple
          prodSetDefaults={{
            code: progObj.code + '_SET',
            name: (progObj.name ?? progObj.code) + ' Product Set',
            startDate: progObj.startDate,
            endDate: progObj.endDate,
          }}
        />
      );
      fieldToFieldDef.code.hide = true;
    }

    fieldToFieldDef.startDate.validators = [
      (value) => {
        return value >= progObj.startDate ?
            null
          : `Start date must be on or after Program start date (${DateUtils.format(progObj.startDate)})`;
      },
      startDateValidator,
    ];
    fieldToFieldDef.endDate.validators = [
      (value) => {
        return value <= progObj.endDate ?
            null
          : `End date must be on or before the Program end date (${DateUtils.format(progObj.endDate)})`;
      },
      endDateValidator,
    ];
  };

  // One Program Product per selected Product, all sharing the rest of the form's values
  const augSaveEntityObjs = (formValues) => {
    const { [PROD_SELECTIONS_FIELD]: selections, ...shared } = formValues;
    const prods =
      selections?.length > 0 ? selections : [{ id: shared.prodId, code: shared.prodCode, name: shared.prodName }];
    return prods.map((prod) => ({
      ...shared,
      prodId: prod.id,
      prodCode: prod.code,
      prodName: prod.name,
      code: progObj.code + '_' + prod.code,
    }));
  };

  return (
    <EntityForm
      entity="PROG_PROD"
      augFieldDefs={augFieldDefs}
      augSaveEntityObjs={embeddedAdd ? augSaveEntityObjs : undefined}
      {...formProps}
    />
  );
};
